import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getCourierBuyerConfirmCopy, getTransactionFlowType, isCourierAutoReleaseEnabled } from '../components/transactionState';
import { Product as D } from '../components/productTokens';
import api, { API_BASE_URL } from '../utils/api';

// The link can show minimal order context publicly, but buyer authentication —
// never possession of the URL — authorises confirm/report mutations.
export default function ConfirmReceipt() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [txn, setTxn] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // 'confirmed' | 'reported'
  const [doneMessage, setDoneMessage] = useState('');
  const [authRequired, setAuthRequired] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/transactions/confirm/${token}`);
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          setError(body.detail || 'This link is invalid or has expired.');
        } else {
          setTxn(await res.json());
        }
      } catch (e) {
        setError('Could not load this order. Please check your connection and try again.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const post = async (path, doneState) => {
    setBusy(true);
    try {
      const response = await api.post(`/transactions/confirm/${token}${path}`);
      setDoneMessage(response.data?.message || 'Your request was recorded.');
      setDone(doneState);
    } catch (e) {
      setAuthRequired(e.response?.status === 401);
      setError(e.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const wrap = { minHeight: '100vh', background: D.bg, backgroundImage: D.bgGlow, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, fontFamily: 'system-ui, sans-serif' };
  const card = { background: D.surfaceSolid, border: `1px solid ${D.border}`, borderRadius: 16, boxShadow: D.shadow, padding: 26, maxWidth: 440, width: '100%' };
  const btn = (bg, color = '#16181B') => ({ width: '100%', padding: '14px 16px', borderRadius: 12, border: 'none', background: bg, color, fontSize: 16, fontWeight: 700, cursor: 'pointer', marginTop: 12 });

  const dateLabel = txn?.auto_release_at
    ? new Date(txn.auto_release_at).toLocaleString('en-ZA', { timeZone: 'Africa/Johannesburg', weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit', hour12: false })
    : null;

  if (loading) return <div style={wrap}><div style={card}><p style={{ textAlign: 'center', color: D.sub }}>Loading your order…</p></div></div>;

  if (error) return (
    <div style={wrap}><div style={card}>
      <h2 style={{ margin: '0 0 8px', color: D.text }}>TrustTrade</h2>
      <p style={{ color: D.sub, lineHeight: 1.5 }}>{error}</p>
      {authRequired && (
        <a
          href={`/login?redirect=${encodeURIComponent(window.location.pathname)}`}
          style={{ ...btn(D.accentGradient), display: 'block', boxSizing: 'border-box', textAlign: 'center', textDecoration: 'none' }}
        >
          Sign in securely as the buyer
        </a>
      )}
    </div></div>
  );

  if (done === 'confirmed') return (
    <div style={wrap}><div style={{ ...card, textAlign: 'center' }}>
      <div style={{ fontSize: 44 }}>✅</div>
      <h2 style={{ margin: '8px 0', color: D.text }}>Thank you!</h2>
      <p style={{ color: D.sub, lineHeight: 1.5 }}>{doneMessage}</p>
    </div></div>
  );

  if (done === 'reported') return (
    <div style={wrap}><div style={{ ...card, textAlign: 'center' }}>
      <div style={{ fontSize: 44 }}>🛟</div>
      <h2 style={{ margin: '8px 0', color: D.text }}>We're on it</h2>
      <p style={{ color: D.sub, lineHeight: 1.5 }}>{doneMessage}</p>
    </div></div>
  );

  if (txn?.released) return (
    <div style={wrap}><div style={{ ...card, textAlign: 'center' }}>
      <div style={{ fontSize: 44 }}>✅</div>
      <h2 style={{ margin: '8px 0', color: D.text }}>All done</h2>
      <p style={{ color: D.sub, lineHeight: 1.5 }}>This payment has already been released. Nothing more to do — you can close this page.</p>
    </div></div>
  );

  return (
    <div style={wrap}>
      <div style={card}>
        <p style={{ fontSize: 12, fontWeight: 800, color: D.accent, letterSpacing: '0.08em', textTransform: 'uppercase', margin: '0 0 6px' }}>TrustTrade</p>
        <h2 style={{ margin: '0 0 4px', color: D.text }}>Did you receive your order?</h2>
        <p style={{ color: D.sub, lineHeight: 1.5, margin: '0 0 16px' }}>
          {txn?.item_description ? <strong>{txn.item_description}</strong> : 'Your order'} from {txn?.seller_name || 'the seller'}.
        </p>

        {(getTransactionFlowType(txn) === 'delivery' && !isCourierAutoReleaseEnabled()) ? (
          <div style={{ background: `${D.success}24`, border: `1px solid ${D.success}4D`, borderRadius: 12, padding: '12px 14px', marginBottom: 4 }}>
            <p style={{ margin: 0, fontSize: 13, color: D.success, lineHeight: 1.5 }}>
              {getCourierBuyerConfirmCopy()}
            </p>
          </div>
        ) : dateLabel ? (
          <div style={{ background: `${D.success}24`, border: `1px solid ${D.success}4D`, borderRadius: 12, padding: '12px 14px', marginBottom: 4 }}>
            <p style={{ margin: 0, fontSize: 13, color: D.success, lineHeight: 1.5 }}>
              If you do nothing, your payment releases automatically on <strong>{dateLabel}</strong>.
            </p>
          </div>
        ) : null}

        <button style={btn(D.accentGradient)} disabled={busy} onClick={() => post('', 'confirmed')}>
          {busy ? 'Please wait…' : '✓ Yes, I received it — release payment'}
        </button>
        <button style={btn(D.hover, D.error)} disabled={busy} onClick={() => post('/report', 'reported')}>
          ⚠ There is a problem with my order
        </button>
        <p style={{ fontSize: 12, color: D.dim, textAlign: 'center', marginTop: 14 }}>
          Only confirm once you actually have your order in hand.
        </p>
      </div>
    </div>
  );
}
